import { gql } from '@apollo/client';
import { workPackageFragments } from '../work-packages';

export const crewMemberFragments = {
  crewMember: gql`
    fragment CrewMember on CrewMember {
      id
      firstName
      lastName
      persona
      imageUrl
      badgeNumber
      discipline
    }
  `,
  crewMemberItem: gql`
    fragment CrewMemberItem on CrewMember {
      id
      firstName
      lastName
      imageUrl
      persona
      shiftType
      subGroupDescription
      planningAiRequest {
        id
        status
        count
        statusMessageData
        statusMessage
      }
    }
  `,
  crewMemberWelder: gql`
    fragment CrewMemberWelder on CrewMember {
      id
      firstName
      lastName
      badgeNumber
      imageUrl
      persona
    }
  `,
  crewMemberCompact: gql`
    fragment CrewMemberCompact on CrewMember {
      id
      firstName
      lastName
      imageUrl
      persona
      subGroupDescription
      badgeNumber
    }
  `,
  crewMemberExpanded: gql`
    fragment CrewMemberExpanded on CrewMember {
      id
      firstName
      lastName
      imageUrl
      persona
      subGroupDescription
      crew {
        id
        crewNumber
      }
    }
  `,
  crewMemberStencil: gql`
    fragment CrewMemberStencil on CrewMember {
      id
      firstName
      lastName
      persona
      imageUrl
      badgeNumber
    }
  `,
  crewMemberWithSupervisor: gql`
    fragment CrewMemberWithSupervisor on CrewMember {
      id
      firstName
      lastName
      persona
      imageUrl
      badgeNumber
      discipline
      supervisor {
        id
        persona
        supervisor {
          id
          persona
        }
      }
    }
  `,
  associatedCrewMember: gql`
    fragment AssociatedCrewMember on AssociatedCrewMembers {
      associatedCrewMembers {
        id
        imageUrl
        firstName
        lastName
        persona
      }
    }
  `,
};

export const GET_CREW_HIERARCHY = gql`
  query search($input: SearchCrewInput!, $paging: PagingInput) {
    crewHierarchy(input: $input, paging: $paging) {
      total
      cursor
      items {
        ...CrewMemberItem
      }
    }
    ${crewMemberFragments.crewMemberItem}
  }
`;

export const GET_CREW_MEMBERS = gql`
  query search($input: SearchCrewMemberInput!, $paging: PagingInput) {
    crewMembers(input: $input, paging: $paging) {
      total
      cursor
      items {
        ...CrewMemberItem
      }
    }
    ${crewMemberFragments.crewMemberItem}
  }
`;

export const GET_WELDERS = gql`
  query search($input: SearchCrewMemberInput!, $paging: PagingInput) {
    crewMembers(input: $input, paging: $paging) {
      total
      cursor
      items {
        ...CrewMemberWelder
      }
    }
    ${crewMemberFragments.crewMemberWelder}
  }
`;

export const GET_CREW_MEMBERS_WITH_SUPERVISOR = gql`
  query search($input: SearchCrewMemberInput!, $paging: PagingInput) {
    crewMembers(input: $input, paging: $paging) {
      total
      cursor
      items {
        ...CrewMemberWelder
        supervisor{
          id
          firstName
          lastName
        }
      }
    }
    ${crewMemberFragments.crewMemberWelder}
  }
`;

export const GET_CREW_MEMBERS_WITH_CREW = gql`
  query search($input:  SearchCrewInput!, $paging: PagingInput) {
    crewHierarchy(input: $input, paging: $paging) {
      total
      cursor
      items {
        ...CrewMemberItem
        hasPendingCrewChangeRequest
        crew {
          id
          crewNumber
        }
      }
    }
    ${crewMemberFragments.crewMemberItem}
  }
`;

export const GET_CREW_MEMBERS_CREW_CHANGE_REQUEST = gql`
  query search($input: SearchCrewInput!, $paging: PagingInput) {
    crewHierarchy(input: $input, paging: $paging) {
      total
      cursor
      items {
        ...CrewMemberItem
        hasPendingCrewChangeRequest
      }
    }
    ${crewMemberFragments.crewMemberItem}
  }
`;

export const ASSIGN_CREW_MEMBER = gql`
  mutation assignCrewMembers($input: AssignCrewMembersInput!) {
    assignCrewMembers(input: $input) {
      erroredWorkPackages {
        error
        workPackage {
          id
        }
      }
      erroredCrewMemberAssignments {
        error
        crewMember {
          id
          firstName
          lastName
        }
        persona
        ownershipType
      }
      workPackage {
        id
        plannedHours
        primaryOwners {
          persona
          crewMember {
            id
            firstName
            lastName
            imageUrl
          }
        }
        secondaryOwners {
          persona
          crewMember {
            id
            firstName
            lastName
            imageUrl
          }
        }
        otherOwners {
          persona
          crewMember {
            id
            firstName
            lastName
            imageUrl
          }
        }
      }
      unplannedMilestones {
        commodityMilestones {
          id
          name
          isExpired
          hours
          plannedStartDate
          plannedEndDate
          currentStatus {
            id
            codeI18n
            rejectionType {
              id
              reason
            }
            comments
            createdAt
            createdBy {
              id
              firstName
              lastName
              imageUrl
            }
          }
          quantity {
            ...Quantity
          }
          commodity {
            materialCommodity {
              id
              tag
              workPackage {
                id
                tag
                plannedHours
              }
              type {
                id
                name
              }
              material {
                mwrs {
                  mwrNumber
                  mwrDate
                  mwrStatus
                }
                mrrNumber
                mrrDate
                deliveryDate
              }
            }
            weld {
              id
              tag
              workPackage {
                id
                tag
                plannedHours
              }
              type {
                id
                name
              }
            }
          }
        }
      }
    }
  }
  ${workPackageFragments.quantity}
`;

export const GET_EARNABLE_HOURS = gql`
  query earnableHours($input: CrewMemberEarnableHoursInput!) {
    earnableHours(input: $input) @connection(key: "pf-earnable-hours", filter: ["input"]) {
      unassignedEarnableHours
      allEarnableHours
      crewMemberEarnableHours {
        crewMemberId
        earnableHours
      }
    }
  }
`;
export const GET_LOB_EMPLOYEES = gql`
  query search($searchKeyword: String!, $paging: PagingInput) {
    lobEmployees(searchKeyword: $searchKeyword, paging: $paging) {
      total
      cursor
      items {
        id
        firstName
        lastName
        isBPSUser
        imageUrl
        bun
        email
        phone
        carrier
        supervisor {
          id
          firstName
          lastName
          bun
        }
      }
    }
  }
`;

export const LOAD_ASSOCIATED_CREWMEMBER = gql`
 query loadAssociatedCrewMember($input: AssociatedCrewMembersInput!){
  loadAssociatedCrewMembers(input: $input){
  ...AssociatedCrewMember
  }
  ${crewMemberFragments.associatedCrewMember}
 }
`;

export const ASSIGN_UNASSIGN_ASSOCIATED_CREW_MEMBER = gql`
 mutation assignUnassignAssociatedCrewMembers($input:AssignUnassignAssociatedCrewMembersInput!){
  assignUnassignAssociatedCrewMembers(input:$input){
    error
    associatedCrewMembers{
      ...AssociatedCrewMember
    }
  }
  ${crewMemberFragments.associatedCrewMember}
 }
`;
